import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import PipelineKpiSegment from './pipeline-kpi-segment';
import { PipelineSegment } from './pipeline-utils';

interface Props {
  kpis: any;
  loading: boolean;
  segment: PipelineSegment;
}

interface Kpi {
  key: string;
  label: string;
  value: string;
  hint: string;
}

const formatDuration = (ms: number): string => {
  if (!ms || isNaN(ms)) return '-';
  if (ms >= 86400000) return `${(ms / 86400000).toFixed(1)}d`;
  return `${(ms / 3600000).toFixed(1)}h`;
};

/**
 * The four headline KPIs, always visible above the board (they used to be hidden
 * inside an accordion). Conversion rates and average durations stay one click away.
 */
const PipelineKpiRow: React.FC<Props> = ({ kpis, loading, segment }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const data = kpis?.[segment];

  if (loading) {
    return (
      <Stack alignItems={'center'} justifyContent={'center'} className={classes.loading}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (!data) {
    return (
      <Box className={classes.empty}>
        <Typography className={classes.emptyText}>{t('pipelineNoKpiData')}</Typography>
      </Box>
    );
  }

  const { total = 0, stageCounts = {}, lostRate, velocity, avgDurations = {} } = data;

  const durationValues = Object.values(avgDurations).map((v) => Number(v));
  const avgStepMs = durationValues.length ? durationValues.reduce((a, b) => a + b, 0) / durationValues.length : 0;

  const lostCount = stageCounts?.lost ?? Math.round((lostRate || 0) * total);
  const stageCount = Object.keys(stageCounts).length;

  const items: Kpi[] = [
    {
      key: 'total',
      label: t('pipelineContactsInPipeline'),
      value: String(total),
      hint: t('pipelineAcrossStages', { count: stageCount }),
    },
    {
      key: 'lostRate',
      label: t('pipelineLostRate'),
      value: `${Math.round((lostRate || 0) * 100)} %`,
      hint: t('pipelineLostOfTotal', { lost: lostCount, total }),
    },
    {
      key: 'velocity',
      label: t('velocity'),
      value: formatDuration(velocity),
      hint: t('pipelineAnswerToClose'),
    },
    {
      key: 'avgStep',
      label: t('pipelineAvgPerStage'),
      value: formatDuration(avgStepMs),
      hint: t('pipelineAcrossAllTransitions'),
    },
  ];

  return (
    <Stack gap={'12px'}>
      <Stack direction={'row'} gap={'12px'} alignItems={'stretch'} flexWrap={'wrap'}>
        {items.map((item) => (
          <Box key={item.key} className={classes.tile}>
            <Typography className={classes.label}>{item.label}</Typography>
            <Typography className={classes.value}>{item.value}</Typography>
            <Typography className={classes.hint}>{item.hint}</Typography>
          </Box>
        ))}
        <Box className={classes.detailsTile}>
          <Typography className={classes.label}>{t('pipelineConversionsAndDurations')}</Typography>
          <Typography className={classes.detailsToggle} onClick={() => setDetailsOpen((open) => !open)}>
            {detailsOpen ? t('pipelineHideDetails') : t('pipelineShowDetails')}
          </Typography>
        </Box>
      </Stack>
      {detailsOpen && <PipelineKpiSegment data={data} />}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  tile: {
    flex: 1,
    minWidth: 180,
    background: '#FFF',
    borderRadius: '12px',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  detailsTile: {
    width: 200,
    minWidth: 200,
    background: '#FFF',
    borderRadius: '12px',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '8px',
  },
  label: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  value: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
  },
  hint: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
  },
  detailsToggle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    cursor: 'pointer',
    width: 'fit-content',
  },
  loading: {
    background: '#FFF',
    borderRadius: '12px',
    padding: theme.spacing(3),
  },
  empty: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '16px 20px',
  },
  emptyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
}));

export default PipelineKpiRow;
